import { Route, Routes } from 'react-router-dom';

import { configValue } from 'config/appConfigUtils';
import { ABExperiment } from 'containers/ABExperiment';
import ErrorBoundary from 'containers/ErrorBoundary';
import { LoggedInStylesLoader } from 'containers/LoggedInStylesLoader';
import { RequireAuthentication } from 'containers/RequireAuthentication';
import SetFlashMessages from 'containers/SetFlashMessages';
import SetGMapsError from 'containers/SetGMapsError';
import { TabContextProvider } from 'containers/TabContext';
import { UpdateRequiredContextProvider } from 'containers/UpdateRequiredContext';
import { useRouterHashScroll } from 'hooks/useRouterHashScroll';
import { useShowUserModals } from 'hooks/useShowUserModals';
import { useTemporaryStorageHandler } from 'hooks/useTemporaryStorageHandler';
import { LayoutProvider } from 'layouts/Layout/LayoutContext';
import { AdminsLinksLoadable } from 'modules/main/pages/AdminsLinks';
import { BuyCreditsLinksLoadable } from 'modules/main/pages/BuyCreditsLinks';
import { CreditsLinksLoadable } from 'modules/main/pages/CreditsLinks';
import { EditOrgLinksLoadable } from 'modules/main/pages/EditOrgLinks';
import { ForgotPasswordPageContainerLoadable } from 'modules/main/pages/ForgotPassword';
import { GradFairsLinksLoadable } from 'modules/main/pages/GradFairsLinks';
import { ImportedListingPageContainerLoadable } from 'modules/main/pages/ImportedListing';
import { IntegrateAtsLinksLoadable } from 'modules/main/pages/IntegrateAtsLinks';
import { InvalidLinkLoadable } from 'modules/main/pages/InvalidLink';
import { InvoiceViewContainerLoadable } from 'modules/main/pages/InvoiceView';
import { InvoicesLinksLoadable } from 'modules/main/pages/InvoicesLinks';
import { LoginPageContainerLoadable } from 'modules/main/pages/Login';
import { NotificationsLinksLoadable } from 'modules/main/pages/NotificationsLinks';
import { RateRecommendedCandidateLoadable } from 'modules/main/pages/RateRecommendedCandidate';
import { ResetPasswordPageContainerLoadable } from 'modules/main/pages/ResetPassword';
import { SignupPageContainerLoadable } from 'modules/main/pages/Signup';
import { UserInviteContainerLoadable } from 'modules/main/pages/UserInvite';
import { OrgDashboardAppLoadable } from 'modules/orgDashboard/OrgDashboardAppLoadable';
import { orgDashboardRouteGroup } from 'modules/orgDashboard/orgDashboard.routes';
import { CompensationSurveyLoadable } from 'modules/salaries/pages/CompensationSurvey/CompensationSurveyLoadable';
import { SalaryReportLoadable } from 'modules/salaries/pages/SalaryReport/SalaryReportLoadable';
import { UserDashboardAppLoadable } from 'modules/userDashboard/UserDashboardAppLoadable';
import { CreateOrgPageContainerLoadable } from 'modules/userDashboard/pages/CreateOrg/CreateOrgPageContainerLoadable';
import { CreateOrgWithGradProgramPageContainerLoadable } from 'modules/userDashboard/pages/CreateOrgWithGradProgram/CreateOrgWithGradProgramPageContainerLoadable';
import { ManageGradProgramPageContainerLoadable } from 'modules/userDashboard/pages/ManageGradProgram/ManageGradProgramPageContainerLoadable';
import { UserDashboardPostAListingPageLoadable } from 'modules/userDashboard/pages/PostAListing/UserDashboardPostAListingPageLoadable';
import { OrgDashboardSelectOrgPageLoadable } from 'modules/userDashboard/pages/SelectOrgDashboard/OrgDashboardSelectOrgPageLoadable';
import { userDashboardRouteGroup } from 'modules/userDashboard/userDashboard.routes';
import { ThemeGlobalStyle } from 'theme/ThemeGlobalStyle';
import { WithRouterParams } from 'utils/WithRouterParams';

import {
  mainExploreSalaryRoute,
  mainGradProgramSegmentedRoutes,
  mainInternshipSegmentedRoutes,
  mainJobSegmentedRoutes,
  mainOrgSegmentedRoutes,
  mainSalarySurveyRoute,
  mainViewUserProfileRoute,
  mainVolopSegmentedRoutes,
} from './main.routes';
import { MainCmsPage } from './pages/Cms/MainCmsPage';
import { ImportedListingPageSource } from './pages/ImportedListing/ImportedListingPageSource';
import { ViewGradProgramPageLoadable } from './pages/ViewGradProgram/ViewGradProgramPageLoadable';
import { ViewInternshipPageLoadable } from './pages/ViewInternship/ViewInternshipPageLoadable';
import { ViewJobPageLoadable } from './pages/ViewJob/ViewJobPageLoadable';
import { ViewOrgPageContainerWrapperLoadable } from './pages/ViewOrg/ViewOrgPageContainerWrapperLoadable';
import { MainViewUserProfilePageLoadable } from './pages/ViewUserProfile/MainViewUserProfilePageLoadable';
import { ViewVolopPageLoadable } from './pages/ViewVolop/ViewVolopPageLoadable';

const SIGN_UP_TO_POST_URL_FOR_LOCALE = {
  en: '/sign-up-to-post',
  es: '/es/orgs/registrate',
  pt: '/pt/orgs/inscreva-se',
};

export default function MainApp() {
  useRouterHashScroll();
  useShowUserModals();
  useTemporaryStorageHandler();

  return (
    <ErrorBoundary>
      <ThemeGlobalStyle />
      <SetGMapsError />
      <SetFlashMessages />
      <LoggedInStylesLoader />
      <LayoutProvider />
      <UpdateRequiredContextProvider>
        <TabContextProvider>
          <Routes>
            <Route path="/amp/*" element={<MainCmsPage />} />
            <Route path="/login" element={<LoginPageContainerLoadable />} />
            <Route path="/signup" element={<SignupPageContainerLoadable />} />
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPageContainerLoadable />}
            />
            <Route
              path="/reset-password/:token"
              element={
                <WithRouterParams
                  render={({ token }) => (
                    <ResetPasswordPageContainerLoadable token={token} />
                  )}
                />
              }
            />
            <Route
              path="/invalid-or-expired-link"
              element={<InvalidLinkLoadable />}
            />
            <Route
              path={mainSalarySurveyRoute.fullPath}
              element={<CompensationSurveyLoadable />}
            />
            <Route
              path={mainExploreSalaryRoute.fullPath}
              element={<SalaryReportLoadable />}
            />
            <Route
              path="/invoices/:invoiceId"
              element={
                <WithRouterParams
                  render={({ invoiceId }) => (
                    <InvoiceViewContainerLoadable invoiceId={invoiceId} />
                  )}
                />
              }
            />
            <Route
              path="/donation-receipts/:invoiceId"
              element={
                <WithRouterParams
                  render={({ invoiceId }) => (
                    <InvoiceViewContainerLoadable
                      invoiceId={invoiceId}
                      isDonation
                    />
                  )}
                />
              }
            />

            {mainOrgSegmentedRoutes.map((segmentedRoute) => (
              <Route
                key={segmentedRoute.fullPath}
                path={segmentedRoute.fullPath}
                element={<ViewOrgPageContainerWrapperLoadable />}
              />
            ))}

            {mainJobSegmentedRoutes.map((segmentedRoute) => (
              <Route
                key={segmentedRoute.fullPath}
                path={segmentedRoute.fullPath}
                element={<ViewJobPageLoadable />}
              />
            ))}

            {mainInternshipSegmentedRoutes.map((segmentedRoute) => (
              <Route
                key={segmentedRoute.fullPath}
                path={segmentedRoute.fullPath}
                element={<ViewInternshipPageLoadable />}
              />
            ))}

            {mainVolopSegmentedRoutes.map((segmentedRoute) => (
              <Route
                key={segmentedRoute.fullPath}
                path={segmentedRoute.fullPath}
                element={<ViewVolopPageLoadable />}
              />
            ))}

            <Route
              path="/:lang/imported-action-opps/:source/:volopId"
              element={
                <WithRouterParams
                  render={({ volopId, source }) => (
                    <ImportedListingPageContainerLoadable
                      source={source as ImportedListingPageSource}
                      listingId={volopId}
                    />
                  )}
                />
              }
            />
            <Route
              path="/user-invites/:inviteId"
              element={
                <WithRouterParams
                  render={({ inviteId }) => (
                    <UserInviteContainerLoadable inviteId={inviteId} />
                  )}
                />
              }
            />
            <Route
              path={userDashboardRouteGroup.wrapperPath}
              element={
                <RequireAuthentication>
                  <UserDashboardAppLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/organizations/create"
              element={
                <RequireAuthentication
                  loginPathname={SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]}
                >
                  <CreateOrgPageContainerLoadable />
                </RequireAuthentication>
              }
            />
            {configValue('idealist', 'gsdEnabled') === 'true' && (
              <Route
                path="/organizations/create-with-grad-program"
                element={
                  <RequireAuthentication>
                    <CreateOrgWithGradProgramPageContainerLoadable />
                  </RequireAuthentication>
                }
              />
            )}
            <Route
              path={orgDashboardRouteGroup.wrapperPath}
              element={
                <RequireAuthentication>
                  <WithRouterParams
                    render={({ orgId }) => (
                      <OrgDashboardAppLoadable orgId={orgId} />
                    )}
                  />
                </RequireAuthentication>
              }
            />

            <Route
              path="/post-a-listing"
              element={
                <ABExperiment
                  experimentId="POST_A_LISTING_REDIRECT"
                  defaultBucket="SIGN_UP_TO_POST"
                  initializeWithDefault
                  render={(bucket) => (
                    <RequireAuthentication
                      loginPathname={
                        bucket === 'SIGN_UP_TO_POST'
                          ? SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                          : getText('/organizations')
                      }
                    >
                      <UserDashboardPostAListingPageLoadable />
                    </RequireAuthentication>
                  )}
                />
              }
            />
            <Route
              path="/select-org"
              element={
                <RequireAuthentication>
                  <OrgDashboardSelectOrgPageLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/manage-grad-program"
              element={
                <RequireAuthentication>
                  <ManageGradProgramPageContainerLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/buy-credits"
              element={
                <RequireAuthentication>
                  <BuyCreditsLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/integrate-ats"
              element={
                <RequireAuthentication>
                  <IntegrateAtsLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/notifications"
              element={
                <RequireAuthentication>
                  <NotificationsLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/credits"
              element={
                <RequireAuthentication>
                  <CreditsLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/admins"
              element={
                <RequireAuthentication>
                  <AdminsLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/edit-org"
              element={
                <RequireAuthentication>
                  <EditOrgLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/gradfairs"
              element={
                <RequireAuthentication>
                  <GradFairsLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/invoices"
              element={
                <RequireAuthentication>
                  <InvoicesLinksLoadable />
                </RequireAuthentication>
              }
            />
            <Route
              path="/email/rate-recommended-candidate/:applicantId/good"
              element={
                <RequireAuthentication>
                  <WithRouterParams
                    render={({ applicantId }) => (
                      <RateRecommendedCandidateLoadable
                        applicantId={applicantId}
                        isGood
                      />
                    )}
                  />
                </RequireAuthentication>
              }
            />
            <Route
              path="/email/rate-recommended-candidate/:applicantId/bad"
              element={
                <RequireAuthentication>
                  <WithRouterParams
                    render={({ applicantId }) => (
                      <RateRecommendedCandidateLoadable
                        applicantId={applicantId}
                        isGood={false}
                      />
                    )}
                  />
                </RequireAuthentication>
              }
            />

            {mainGradProgramSegmentedRoutes.map((segmentedRoute) => (
              <Route
                key={segmentedRoute.fullPath}
                path={segmentedRoute.fullPath}
                element={<ViewGradProgramPageLoadable />}
              />
            ))}

            <Route
              path={mainViewUserProfileRoute.fullPath}
              element={<MainViewUserProfilePageLoadable />}
            />

            <Route path="/*" element={<MainCmsPage />} />
          </Routes>
        </TabContextProvider>
      </UpdateRequiredContextProvider>
    </ErrorBoundary>
  );
}
